import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import config from '../config';
import { useAdminApi } from './hooks/useAdminApi';
import { AdminBreadcrumbs } from './components/AdminUiPrimitives';
import { AdminPageHeader } from './components/AdminMasterDetail';
import SessionHub, { SessionHubData } from '../components/sessions/SessionHub';

const API_BASE_URL = `${config.apiBaseUrl}/api`;

const ManageSessionDetail: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const sessionId = Number(params.sessionId ?? params.id ?? 0);
  const { user } = useAuth0();
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();

  const [session, setSession] = useState<SessionHubData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joinMessage, setJoinMessage] = useState<string | null>(null);

  const currentUserName = useMemo(() => String(user?.name || user?.nickname || user?.email || 'Current user'), [user]);

  const loadSession = useCallback(async () => {
    if (!hasActiveOrganization || !Number.isFinite(sessionId) || sessionId <= 0) {
      setError('No valid session selected.');
      setSession(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);
      const response = await axios.get<SessionHubData>(`${API_BASE_URL}/schedules/sessions/${sessionId}`, { headers });
      setSession(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load session details.');
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [getManagementToken, getOrganizationHeaders, hasActiveOrganization, sessionId]);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  const handleJoin = async () => {
    if (!session) {
      return;
    }

    setJoining(true);
    setJoinMessage(null);
    try {
      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);
      const response = await axios.post(`${API_BASE_URL}/schedules/sessions/${session.id}/join`, {}, { headers });
      const data = response.data as { meetingUrl?: string; alreadyJoined?: boolean };

      setJoinMessage(data?.alreadyJoined
        ? 'You were already marked as joined. Opening the meeting now.'
        : 'You have been marked as joined. Opening the meeting now.');

      if (data?.meetingUrl) {
        window.open(data.meetingUrl, '_blank', 'noopener,noreferrer');
      }

      await loadSession();
    } catch (err: any) {
      setJoinMessage(err?.response?.data?.message || err?.message || 'Failed to join session.');
    } finally {
      setJoining(false);
    }
  };

  const handleStartSession = async () => {
    if (!session) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);
      await axios.post(`${API_BASE_URL}/schedules/sessions/${session.id}/start`, {}, { headers });
      await loadSession();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to start session.');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteSession = async () => {
    if (!session) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);
      await axios.post(`${API_BASE_URL}/schedules/sessions/${session.id}/complete`, {}, { headers });
      await loadSession();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to finish session.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ width: '100%', display: 'grid', gap: 16 }}>
      <AdminBreadcrumbs
        items={[
          { label: 'Administration', to: '/analytics' },
          { label: 'All Sessions', to: '/manage-sessions/all' },
          { label: 'Session Hub', isCurrent: true },
        ]}
      />

      <AdminPageHeader
        title={session ? (session.title || `Session ${session.id}`) : 'Session Hub'}
        subtitle={session ? `${session.courseName || session.classroomName} • ${session.teacherDisplayName}` : 'Teacher and student session hub'}
        actions={[
          {
            key: 'back',
            label: 'Back to list',
            variant: 'secondary',
            onClick: () => navigate('/manage-sessions/all'),
          },
        ]}
      />

      {!hasActiveOrganization && (
        <div style={{ borderRadius: 10, border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', padding: '10px 12px', fontSize: '0.84rem' }}>
          No organization selected. Please switch organization first.
        </div>
      )}

      {error && (
        <div style={{ borderRadius: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#b91c1c', padding: '10px 12px', fontSize: '0.84rem' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ borderRadius: 12, background: '#fff', border: '1px solid #dfe8f8', padding: 18, color: '#475569' }}>
          Loading session details...
        </div>
      ) : session ? (
        <SessionHub
          session={session}
          currentUserName={currentUserName}
          currentUserKey={String(user?.sub || user?.email || currentUserName)}
          getDiscussionAccessToken={getManagementToken}
          getDiscussionHeaders={async () => {
            const token = await getManagementToken();
            return getOrganizationHeaders(token);
          }}
          canManageSession
          joining={joining}
          saving={saving}
          joinMessage={joinMessage}
          onJoin={handleJoin}
          onStartSession={handleStartSession}
          onCompleteSession={handleCompleteSession}
        />
      ) : null}
    </div>
  );
};

export default ManageSessionDetail;